// Pager for the ad-hoc report preview tables. It renders as a footer *inside*
// the table card — hence the top border and the absence of a rounded outline of
// its own. Deliberately separate from src/Components/Pagination, which wears a
// different (circular, range-on-the-right) design and is shared by the WO
// History and Pos lists; those are not meant to change with this one.
//
// `currentPage` is 1-based, matching src/Components/Pagination's contract, so
// call sites keep passing `page + 1` and receiving 1-based pages back.

// Which page numbers to show, given there isn't room for all of them.
// Returns page numbers interleaved with "..." gaps, e.g. [1, 2, 3, "...", 10].
const buildVisiblePages = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, "...", totalPages];
  }
  if (currentPage >= totalPages - 2) {
    return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  }
  // Somewhere in the middle: keep the current page flanked by its neighbours.
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

const STEP_BUTTON_BASE =
  "px-[14px] py-2 bg-white rounded-[8px] shadow-[0px_1px_2px_rgba(0,0,0,0.05)] outline outline-1 -outline-offset-1 font-OpenSansSemiBold text-[14px] leading-[20px]";

const StepButton = ({ label, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`${STEP_BUTTON_BASE} ${
      disabled
        ? "outline-[#E5E8F3] text-[#C9CEE2] cursor-not-allowed"
        : "outline-[#C9CEE2] text-[#1A2332] hover:bg-[#F7F9FC]"
    }`}
  >
    {label}
  </button>
);

function PreviewPagination({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  pageSize = 10,
  onPageChange = () => {},
}) {
  const safeTotalPages = Math.max(totalPages, 1);
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), safeTotalPages);

  const goToPage = (page) => {
    const nextPage = Math.min(Math.max(page, 1), safeTotalPages);
    if (nextPage !== safeCurrentPage) {
      onPageChange(nextPage);
    }
  };

  // The 1-based row range this page covers. The last page is usually short, so
  // the end is clamped to the total rather than assuming a full page.
  const rangeStart = totalItems === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safeCurrentPage * pageSize, totalItems);

  const label = "font-OpenSansRegular text-[14px] leading-[20px] text-[#868DA6]";
  const value = "font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332]";

  return (
    <div className="w-full pt-3 pb-4 px-6 border-t-[1px] border-[#E5E8F3] flex items-center justify-between gap-4">
      <p>
        <span className={label}>Showing </span>
        <span className={value}>{rangeStart}</span>
        <span className={label}> to </span>
        <span className={value}>{rangeEnd}</span>
        <span className={label}> of </span>
        <span className={value}>{totalItems}</span>
        <span className={label}> results</span>
      </p>

      <div className="flex items-center gap-3">
        <StepButton
          label="Previous"
          onClick={() => goToPage(safeCurrentPage - 1)}
          disabled={safeCurrentPage <= 1}
        />

        <div className="flex items-start gap-[2px]">
          {buildVisiblePages(safeCurrentPage, safeTotalPages).map((page, index) => {
            if (page === "...") {
              return (
                <span
                  key={`gap-${index}`}
                  className="w-10 h-10 flex items-center justify-center font-OpenSansSemiBold text-[14px] leading-[20px] text-[#868DA6]"
                  aria-hidden="true"
                >
                  ...
                </span>
              );
            }

            const isActive = page === safeCurrentPage;
            return (
              <button
                key={page}
                type="button"
                onClick={() => goToPage(page)}
                aria-current={isActive ? "page" : undefined}
                aria-label={`Go to page ${page}`}
                className={`w-10 h-10 rounded-[8px] flex items-center justify-center text-center font-OpenSansSemiBold text-[14px] leading-[20px] ${
                  isActive ? "bg-[#F5F9FF] text-[#0065FD]" : "text-[#868DA6] hover:bg-[#F5F9FF]"
                }`}
              >
                {page}
              </button>
            );
          })}
        </div>

        <StepButton
          label="Next"
          onClick={() => goToPage(safeCurrentPage + 1)}
          disabled={safeCurrentPage >= safeTotalPages}
        />
      </div>
    </div>
  );
}

export default PreviewPagination;
